import React from 'react';
import { motion } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaRegEnvelope, FaWhatsapp, FaInstagram, FaTiktok } from 'react-icons/fa';
import { IoLogoFacebook } from 'react-icons/io';
import { LuPhone } from 'react-icons/lu';
import { SlLocationPin } from 'react-icons/sl';
import logo from '../assets/2.png';

// Animation variants
const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const Footer = () => {
    // Important: use the 'footer' namespace
    const { t } = useTranslation('footer');

    const currentYear = new Date().getFullYear();

    const navigationLinks = [
        { link: '/', key: 'home' },
        { link: '/about-us', key: 'aboutUs' },
        { link: '/our-products', key: 'jewelleriesStones' },
        { link: '/gem-history', key: 'gemHistory' },
        { link: '/blogs', key: 'blogs' },
        { link: '/contact-us', key: 'contactUs' },
    ];

    return (
        <div className="bg-[#1F1F1F] font-lato text-white">
            {/* Main Footer Content */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={textVariants}
                className="px-5 pt-4"
            >
                <div className="h-auto rounded-t-lg border-l-[1px] border-r-[1px] border-t-[1px] border-[#333333] py-10">
                    <div className="container justify-between lg:flex">
                        {/* Column 1: Logo, Social Icons, Description */}
                        <div className="w-full lg:w-1/3">
                            <div className="w-full lg:w-[78%]">
                                <div className="flex justify-center">
                                    <img
                                        src={logo}
                                        alt="Marino Gems Logo"
                                        width={100}
                                        height={80}
                                        className="rounded-lg object-cover"
                                    />
                                </div>
                                <div className="my-5 flex justify-center gap-5">
                                    <a
                                        href="https://web.facebook.com/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Visit our Facebook page"
                                        className="transition-all duration-300 hover:scale-125 hover:text-[#5c4033]"
                                    >
                                        <IoLogoFacebook size={20} className="text-[#BF943A]" />
                                    </a>
                                    <a
                                        href="https://www.instagram.com/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Visit our Instagram page"
                                        className="transition-all duration-300 hover:scale-125 hover:text-[#5c4033]"
                                    >
                                        <FaInstagram size={20} className="text-[#BF943A]" />
                                    </a>
                                    <a
                                        href="https://wa.me/+94777939655"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Contact us on WhatsApp"
                                        className="transition-all duration-300 hover:scale-125 hover:text-[#5c4033]"
                                    >
                                        <FaWhatsapp size={20} className="text-[#BF943A]" />
                                    </a>
                                    <a
                                        href="https://www.tiktok.com/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Visit our TikTok page"
                                        className="transition-all duration-300 hover:scale-125 hover:text-[#5c4033]"
                                    >
                                        <FaTiktok size={20} className="text-[#BF943A]" />
                                    </a>
                                    <a
                                        href="mailto:marinogemsandjewellery@gmail.com"
                                        aria-label="Email us"
                                        className="transition-all duration-300 hover:scale-125 hover:text-[#5c4033]"
                                    >
                                        <FaRegEnvelope size={20} className="text-[#BF943A]" />
                                    </a>
                                </div>
                                <div className="text-center text-[14px] leading-[22px] sm:text-[16px] sm:leading-[25px]">
                                    {t('description')}
                                </div>
                            </div>
                        </div>

                        {/* Column 2: Navigation Links */}
                        <div className="my-8 lg:mt-0 lg:w-1/3 lg:pl-14">
                            <div className="text-center text-[16px] font-medium text-[#BF943A] sm:text-[18px] lg:text-left">
                                {t('linksTitle')}
                            </div>
                            <div className="mt-5 flex flex-col items-center gap-2 leading-8 lg:items-start">
                                {navigationLinks.map((item, index) => (
                                    <NavLink
                                        key={index}
                                        to={item.link}
                                        className="w-[180px] text-center text-[14px] font-medium transition-colors duration-200 hover:text-[#BF943A] sm:text-[16px] lg:text-left"
                                        style={({ isActive }) => ({
                                            textDecoration: isActive ? 'underline' : 'none',
                                            textUnderlineOffset: '4px',
                                        })}
                                    >
                                        {t(item.key)}   {/* ← these come from 'navbar' namespace */}
                                    </NavLink>
                                ))}
                            </div>
                        </div>

                        {/* Column 3: Contact Info */}
                        <div className="flex flex-col items-center text-center lg:w-1/3 lg:items-start lg:pl-14 lg:text-left">
                            <div className="text-[16px] font-medium text-[#BF943A] sm:text-[18px]">
                                {t('contactTitle')}
                            </div>
                            <div className="mt-5 leading-10">
                                <div className="flex items-center justify-center gap-3 lg:justify-start">
                                    <LuPhone size={20} className="text-[#BF943A]" />
                                    <a
                                        href="tel:+94777939655"
                                        className="text-[14px] font-medium transition-colors duration-200 hover:text-[#BF943A] sm:text-[16px]"
                                    >
                                        +94 812 221 779
                                    </a>
                                </div>
                                <div className="flex items-center justify-center gap-3 lg:justify-start">
                                    <FaRegEnvelope size={20} className="text-[#BF943A]" />
                                    <a
                                        href="mailto:marinogemsandjewellery@gmail.com"
                                        className="text-[14px] font-medium transition-colors duration-200 hover:text-[#BF943A] sm:text-[16px]"
                                    >
                                        marinogemsandjewellery@gmail.com
                                    </a>
                                </div>
                                <div className="flex items-center justify-center gap-3 lg:justify-start">
                                    <SlLocationPin size={20} className="text-[#BF943A]" />
                                    <a
                                        href="https://maps.app.goo.gl/pvY6XrmbrpuM46RS7"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[14px] font-medium transition-colors duration-200 hover:text-[#BF943A] sm:text-[16px]"
                                    >
                                        View Location
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Bottom Bar: Copyright & Developer */}
            <div className="flex flex-col items-center justify-center gap-1 bg-[#636363] text-white min-h-[70px] px-4 py-3 sm:min-h-[50px] sm:py-2">
                <motion.span
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={textVariants}
                    className="text-[12px] font-medium sm:text-[14px]"
                >
                    {t('copyright', { year: currentYear })}
                </motion.span>
                <motion.span
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={textVariants}
                    className="text-[12px] font-medium sm:text-[14px]"
                >
                    {t('developedBy')}{' '}
                    <a
                        href="tel:+94764608225"
                        className="transition-colors duration-200 hover:text-[#BF943A]"
                    >
                        Redev Solution +94 764 608 225
                    </a>
                </motion.span>
            </div>
        </div>
    );
};

export default Footer;